import { httpsCallable } from "firebase/functions";
import { getIdToken } from "firebase/auth";
import type { EstadoDaAssinatura } from "@nhomi/core";
import { auth, functions } from "@/lib/firebase";
import { comprar, comprasAtivas } from "@/lib/billing";

/* ============================================================
   O que a tela faz com uma compra

   A tela NUNCA diz ao servidor "virei assinante". Ela manda o token que
   a loja devolveu, e o servidor pergunta à Play quanto aquele token
   vale. Toda a segurança da assinatura está nessa inversão — o resto
   deste arquivo é encanamento.
   ============================================================ */

export interface Confirmacao {
  assinante: boolean;
  estado: EstadoDaAssinatura;
  ate: string;
}

const chamarConfirmar = httpsCallable<{ token: string }, Confirmacao>(
  functions, "nhomiConfirmarCompra",
);

/**
 * Manda o token para o servidor e espera o veredito da Play.
 *
 * Depois que ele volta positivo, força um token de autenticação novo. O
 * motivo é o claim: `assinante` entra no token do Firebase, e um token
 * já emitido não muda sozinho — a próxima renovação natural pode levar
 * até uma HORA. Sem o `getIdToken(true)` aqui, a pessoa acabaria de
 * pagar, veria a tela dizer "assinante" (porque a tela lê o campo, que
 * muda na hora) e levaria "sem permissão" da regra do Firestore na
 * primeira coisa que tentasse fazer.
 *
 * É a mesma razão pela qual o plano mora em dois lugares, vista do lado
 * de cá: os dois precisam chegar juntos, e este é o empurrão que faz o
 * segundo chegar.
 */
export async function confirmarCompra(token: string): Promise<Confirmacao> {
  const { data } = await chamarConfirmar({ token });
  if (data.assinante && auth.currentUser) {
    await getIdToken(auth.currentUser, true).catch(() => {});
  }
  return data;
}

/** Compra, e só responde depois que o servidor confirmou. */
/* ============================================================
   O token fica guardado até o servidor confirmar

   ISTO NASCEU DE UM CASO REAL, e vale contar porque o buraco não é
   óbvio: a compra passou na Play, o `confirmarCompra` falhou por um
   motivo de servidor, e a pessoa ficou paga E sem assinatura — sem
   caminho de volta. A Play recusa comprar de novo ("você já tem uma
   assinatura"), e `listPurchases`, que existe justamente para recuperar
   o token, depende da Digital Goods API, que no aparelho estava fora.

   O aviso da Play também não salva: ele chega com o TOKEN e mais nada, e
   sem o vínculo token→uid o servidor não sabe de quem é a compra. Esse
   vínculo é criado no `confirmarCompra` — o passo que falhou.

   Guardar o token aqui quebra essa dependência circular. Ele é o
   comprovante, e um comprovante que some no primeiro erro de rede não é
   comprovante. Sai daqui só quando o servidor confirma.

   `localStorage` e não memória: o caso que importa é justamente aquele em
   que o app fecha no meio. E com try/catch em volta porque aba anônima e
   armazenamento bloqueado existem — falhar em guardar não pode derrubar
   uma compra que deu certo.
   ============================================================ */

const GUARDADO = "nhomi.compra.pendente";

const guardarToken = (token: string): void => {
  try { localStorage.setItem(GUARDADO, token); } catch { /* sem storage */ }
};

const esquecerToken = (): void => {
  try { localStorage.removeItem(GUARDADO); } catch { /* sem storage */ }
};

const tokenGuardado = (): string => {
  try { return localStorage.getItem(GUARDADO) ?? ""; } catch { return ""; }
};

export async function assinar(): Promise<
  { ok: true; confirmacao: Confirmacao }
  | { ok: false; motivo: "indisponivel" | "cancelada" | "falhou"; detalhe?: string }
> {
  const compra = await comprar();
  if (!compra.ok) return compra;

  /* ANTES de tentar confirmar: se a confirmação falhar, é este token que
     dá o caminho de volta. */
  guardarToken(compra.token);

  try {
    const confirmacao = await confirmarCompra(compra.token);
    esquecerToken();
    /* A folha da Play só fecha depois desta linha, e ela só roda depois
       do servidor ter falado. Fechar como sucesso antes disso seria
       dizer "pronto" para quem talvez não tenha virado assinante. */
    await compra.concluir(confirmacao.assinante);
    return { ok: true, confirmacao };
  } catch (err) {
    await compra.concluir(false);
    return { ok: false, motivo: "falhou", detalhe: (err as Error)?.message };
  }
}

/**
 * Reconcilia, em silêncio, o que a loja já vendeu.
 *
 * Roda na abertura do app e não mostra nada — nem sucesso, nem erro.
 * Conserta dois casos que a pessoa não tem como resolver sozinha: o
 * aparelho novo (ou a reinstalação), onde a compra existe na conta
 * Google e não neste banco; e a compra órfã, onde o pagamento passou mas
 * o app fechou antes de confirmar.
 *
 * Falha sem barulho de propósito: quem abriu o app não pediu nada, e um
 * erro de rede aqui não é assunto dele. Se a reconciliação não acontecer
 * hoje, acontece na próxima abertura.
 */
export async function restaurarCompras(): Promise<void> {
  /* O guardado vem PRIMEIRO, e é o que funciona quando a Digital Goods
     API está fora — `comprasAtivas()` depende dela e devolve lista vazia
     nesse caso, que é exatamente quando alguém precisa ser recuperado. */
  const pendente = tokenGuardado();
  if (pendente) {
    await confirmarCompra(pendente)
      .then(() => esquecerToken())
      .catch((err: unknown) => {
        /* Token que a Play não reconhece não vai ser reconhecido nunca —
           insistir a cada abertura seria uma chamada por vida. Erro de
           servidor, esse a gente guarda e tenta de novo. */
        const codigo = (err as { code?: string })?.code ?? "";
        if (codigo.endsWith("not-found") || codigo.endsWith("failed-precondition")) {
          esquecerToken();
        }
      });
  }

  for (const c of await comprasAtivas()) {
    await confirmarCompra(c.purchaseToken).catch(() => {});
  }
}
